import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customersService } from '../services/apiServices';
import { Customer, PaginatedResponse } from '../types';

// Get all customers
export const useCustomers = (params?: any) => {
  return useQuery<PaginatedResponse<Customer>>({
    queryKey: ['customers', params],
    queryFn: () => customersService.getAll(params),
  });
};

// Get single customer
export const useCustomer = (id: number) => {
  return useQuery<Customer>({
    queryKey: ['customer', id],
    queryFn: () => customersService.getById(id),
    enabled: !!id,
  });
};

// Create customer mutation
export const useCreateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Customer>) => customersService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
};

// Update customer mutation
export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Customer> }) =>
      customersService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer', variables.id] });
    },
  });
};

// Delete customer mutation
export const useDeleteCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => customersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
};

// Get customer stats
export const useCustomerStats = () => {
  return useQuery({
    queryKey: ['customers', 'stats'],
    queryFn: () => customersService.getStats(),
  });
};

// SMS logs query
export const useCustomerSmsLogs = (params?: {
  customer_id?: number;
  status?: string;
  trigger?: string;
  date_from?: string;
  date_to?: string;
}) => {
  return useQuery({
    queryKey: ['customers', 'sms-logs', params],
    queryFn: () => customersService.getSmsLogs(params),
  });
};

// Resend SMS receipt mutation
export const useResendCustomerSms = () => {
  return useMutation({
    mutationFn: ({ id, date }: { id: number; date: string }) =>
      customersService.resendSms(id, date),
  });
};

// Get customers with outstanding balances
export const useOutstandingCustomers = () => {
  return useQuery({
    queryKey: ['customers', 'outstanding'],
    queryFn: () => customersService.getOutstanding(),
  });
};
